import { Injectable, inject } from '@angular/core';

import { AuthService } from '@auth0/auth0-angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { interval, Observable, of, timer } from 'rxjs';
import {
  catchError,
  delayWhen,
  exhaustMap,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { HomeActions } from './home.actions';
import { ActivityService } from '../../features/home/services/activity.service';
import { ProductUpdateService } from '../../features/home/services/product-update.service';
import { TransactionService } from '../../features/home/services/transaction.service';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { RootState } from '../root-state.model';

@Injectable()
export class HomeEffects {
  private readonly transactionService = inject(TransactionService);
  private readonly productUpdatesService = inject(ProductUpdateService);
  private readonly activityService = inject(ActivityService);
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly store = inject<Store<RootState>>(Store);

  private readonly refreshTokenFreq = 30000;

  loadRecentActivitiesEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.LoadRecentActivities),
      withLatestFrom(this.store),
      switchMap(() =>
        this.activityService.getActivityRecentSummary().pipe(
          map((activityRecentSummary) =>
            HomeActions.LoadRecentActivitiesSuccess({ activityRecentSummary })
          ),
          catchError((error) => {
            console.error(error);
            return of(HomeActions.LoadRecentActivitiesFail({ error }));
          })
        )
      )
    )
  );

  loadProductUpdatesEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.LoadProductUpdates),
      withLatestFrom(this.store),
      filter(([action, store]) => !store.home.productUpdates),
      switchMap(() =>
        this.productUpdatesService.getAll().pipe(
          map((list) => HomeActions.LoadProductUpdatesSuccess({ list })),
          reportEffectError(HomeActions.LoadProductUpdatesFail)
        )
      )
    )
  );

  startRefreshTokenEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HomeActions.StartRefreshToken),
        exhaustMap(() => {
          console.debug(
            `[HomeActions.StartRefreshToken] Timer started (freq ${this.refreshTokenFreq} ms)`
          );
          return timer(0, this.refreshTokenFreq);
        }),
        takeUntil(
          this.actions$.pipe(
            ofType(HomeActions.StopRefreshToken),
            tap(() => {
              console.debug(`[HomeActions.StopRefreshToken] received`);
            })
          )
        ),
        delayWhen((time) => (time === 0 ? interval(10_000) : of(time))),
        switchMap((time) => {
          const startTokenFetch = Date.now();
          console.debug(
            `[HomeActions.StartRefreshToken] Fetching token (cacheMode ${time === 0 ? 'off' : 'on'})`
          );
          return this.authService
            .getAccessTokenSilently({ cacheMode: time === 0 ? 'off' : 'on' })
            .pipe(
              tap(() => {
                console.debug(
                  `[HomeActions.StartRefreshToken] Token fetched in ${Date.now() - startTokenFetch} ms`
                );
              })
            );
        }),
        tap((accessToken) => {
          localStorage.setItem('semeris-access-token', JSON.stringify(accessToken));
        })
      ),
    { dispatch: false }
  );
}
